"use client";

import { msg, plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { getToolName } from "ai";
import { ChevronRightIcon } from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { getToolDisplayMeta } from "@/lib/ai/tools/ui/display-meta";
import { cn } from "@/lib/utils";
import type { ParallelToolBatch } from "./group-activity-items-for-display";
import type { ActivityToolPresentation } from "./render-model";

/** batch の実行状態を、成功・失敗・拒否を混同しない短い文言へ変換する。 */
export function getParallelToolBatchSummary(
  batch: ParallelToolBatch,
  presentation: ActivityToolPresentation,
  i18n: ReturnType<typeof useLingui>["i18n"],
): { title: string; progress: string } {
  const containsNonReadTool = batch.tools.some(
    (tool) => getToolDisplayMeta(getToolName(tool.part)).effect !== "read",
  );
  const title =
    batch.hasApproval || containsNonReadTool
      ? i18n._(msg`Multiple operations`)
      : presentation === "live"
        ? i18n._(msg`Running ${batch.totalCount} tools in parallel`)
        : i18n._(msg`Ran ${batch.totalCount} tools in parallel`);

  if (presentation === "live") {
    const progressParts = [
      i18n._(msg`${batch.completedCount} of ${batch.totalCount} completed`),
      batch.errorCount > 0
        ? i18n._(
            msg({
              message: plural(batch.errorCount, {
                one: "# error",
                other: "# errors",
              }),
            }),
          )
        : null,
      batch.deniedCount > 0 ? i18n._(msg`${batch.deniedCount} denied`) : null,
    ].filter((part): part is string => part !== null);

    return {
      title,
      progress: progressParts.join(" · "),
    };
  }

  const resultParts = [
    i18n._(msg`${batch.completedCount} completed`),
    batch.errorCount > 0
      ? i18n._(
          msg({
            message: plural(batch.errorCount, {
              one: "# error",
              other: "# errors",
            }),
          }),
        )
      : null,
    batch.deniedCount > 0 ? i18n._(msg`${batch.deniedCount} denied`) : null,
  ].filter((part): part is string => part !== null);

  return { title, progress: resultParts.join(" · ") };
}

/**
 * 同一 model step の Tool 群を一つの disclosure として表示し、
 * batch を開くまで個別 Tool subtree を構築しない。
 */
export function ParallelToolBatchDisclosure({
  batch,
  onFocusRestored,
  onOpenChange,
  onTriggerBlur,
  onTriggerFocus,
  open: controlledOpen,
  presentation,
  renderTool,
  restoreFocus = false,
}: {
  batch: ParallelToolBatch;
  onFocusRestored?: () => void;
  onOpenChange?: (open: boolean) => void;
  onTriggerBlur?: () => void;
  onTriggerFocus?: () => void;
  open?: boolean;
  presentation: ActivityToolPresentation;
  renderTool: (
    partIndex: number,
    presentation: ActivityToolPresentation,
  ) => ReactNode;
  restoreFocus?: boolean;
}) {
  const { i18n } = useLingui();
  const contentId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const onToggle = useCallback(() => {
    const nextOpen = !open;
    if (controlledOpen === undefined) setLocalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }, [controlledOpen, onOpenChange, open]);
  const summary = getParallelToolBatchSummary(batch, presentation, i18n);
  const live = presentation === "live";

  useLayoutEffect(() => {
    if (!restoreFocus) return;
    triggerRef.current?.focus({ preventScroll: true });
    onFocusRestored?.();
  }, [onFocusRestored, restoreFocus]);

  return (
    <div
      className="min-w-0"
      data-parallel-tool-batch={presentation}
      data-parallel-tool-batch-id={batch.batchId}
    >
      <button
        aria-controls={open ? contentId : undefined}
        aria-expanded={open}
        className={cn(
          "flex w-full min-w-0 items-center gap-1.5 rounded-sm text-left transition-colors hover:bg-muted/35",
          live ? "min-h-10 px-0 py-1" : "min-h-7 py-0.5 pr-1",
        )}
        data-preserve-chat-scroll-on-expand=""
        onBlur={onTriggerBlur}
        onClick={onToggle}
        onFocus={onTriggerFocus}
        ref={triggerRef}
        type="button"
      >
        <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden text-[13px] leading-5">
          <span className="truncate text-foreground/85">{summary.title}</span>
          <span className="min-w-0 truncate text-muted-foreground">
            · {summary.progress}
          </span>
        </span>
        <ChevronRightIcon
          aria-hidden="true"
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90",
          )}
        />
      </button>
      {open ? (
        <div
          className="mt-1.5 ml-1 space-y-1 border-border/60 border-l pl-3"
          data-parallel-tool-batch-content=""
          id={contentId}
        >
          {batch.tools.map((tool) => (
            <div
              data-parallel-tool-call-id={tool.part.toolCallId}
              key={tool.part.toolCallId}
            >
              {renderTool(tool.partIndex, presentation)}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
